import React, { useEffect, useRef, useCallback } from 'react';

// Selector for elements that can receive keyboard focus inside the dialog.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Accessible modal wrapper (no external dependencies).
 *
 * Handles: role="dialog" + aria-modal, Escape to close, overlay-click to close,
 * focus trapping (Tab / Shift+Tab cycle), autofocus on open, focus restore on
 * close, and body scroll lock. Callers control mounting (render <Modal> only
 * when open) and supply their own overlay/content class names for styling.
 */
const Modal = ({
  onClose,
  children,
  labelledBy,
  describedBy,
  ariaLabel,
  overlayClassName = 'modal-overlay',
  contentClassName = 'modal-content',
  initialFocusRef,
}) => {
  const contentRef = useRef(null);
  const previouslyFocused = useRef(null);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose?.();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = contentRef.current?.querySelectorAll(FOCUSABLE_SELECTOR);
    if (!focusables || focusables.length === 0) {
      // Nothing to focus — keep focus on the dialog itself.
      event.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !contentRef.current.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, [onClose]);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;

    // Lock background scroll while the dialog is open.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the dialog.
    const target =
      initialFocusRef?.current ||
      contentRef.current?.querySelector(FOCUSABLE_SELECTOR) ||
      contentRef.current;
    target?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, []);

  return (
    <div className={overlayClassName} onClick={onClose} onKeyDown={handleKeyDown}>
      <div
        ref={contentRef}
        className={contentClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

export default Modal;
